import React from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { formatMoney, formatPercent } from '@/utils/format';

const { Text, Title, Paragraph } = Typography;

export interface Wave6Ctx {
  estimateId: string;
  revisionId: string;
  readOnly: boolean;
}

function money(n: unknown) {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return formatMoney(v, { display: 'plain', maximumFractionDigits: 2 });
}

function pct(n: unknown) {
  if (n == null || n === '') return '—';
  return formatPercent(Number(n), { maximumFractionDigits: 2 });
}

export function useWave6Pricing(ctx: Wave6Ctx | null) {
  const enabled = !!ctx?.estimateId && !!ctx?.revisionId;
  const summaryQ = useQuery({
    queryKey: ['epc-est-pricing-sum', ctx?.estimateId, ctx?.revisionId],
    queryFn: () => epcEstimationService.pricingSummary(ctx!.estimateId, ctx!.revisionId),
    enabled,
  });
  const clientQ = useQuery({
    queryKey: ['epc-est-client-pricing', ctx?.estimateId, ctx?.revisionId],
    queryFn: () => epcEstimationService.listClientPricing(ctx!.estimateId, ctx!.revisionId),
    enabled,
  });
  const recQ = useQuery({
    queryKey: ['epc-est-pricing-rec', ctx?.estimateId, ctx?.revisionId],
    queryFn: () => epcEstimationService.pricingReconciliation(ctx!.estimateId, ctx!.revisionId),
    enabled,
  });
  return { summaryQ, clientQ, recQ };
}

export function StrategyPanel({ ctx }: { ctx: Wave6Ctx }) {
  const qc = useQueryClient();
  const { summaryQ } = useWave6Pricing(ctx);
  const [form] = Form.useForm();
  const p = summaryQ.data?.pricing;
  React.useEffect(() => {
    if (p) {
      form.setFieldsValue({
        pricing_method: p.pricing_method,
        markup_percent: p.markup_percent,
        target_margin_percent: p.target_margin_percent,
        manual_bid_price: p.manual_bid_price,
        manual_reason: p.manual_reason,
        commercial_adjustment_amount: p.commercial_adjustment_amount,
        commercial_adjustment_reason: p.commercial_adjustment_reason,
        negotiation_reserve_amount: p.negotiation_reserve_amount,
        discount_type: p.discount_type,
        discount_percent: p.discount_percent,
        discount_amount: p.discount_amount,
      });
    }
  }, [p, form]);
  const m = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.putPricing(ctx.estimateId, ctx.revisionId, v),
    onSuccess: () => {
      message.success('Pricing saved');
      qc.invalidateQueries({ queryKey: ['epc-est-pricing-sum'] });
      qc.invalidateQueries({ queryKey: ['epc-est-pricing-rec'] });
      qc.invalidateQueries({ queryKey: ['epc-est-readiness'] });
      qc.invalidateQueries({ queryKey: ['epc-est-total'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Failed'),
  });
  const bridge = summaryQ.data?.commercial_bridge;
  const method = Form.useWatch('pricing_method', form) || p?.pricing_method;
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Alert
        type="info"
        showIcon
        message="Pricing methods"
        description="Markup adds % to cost. Target Margin calculates sell so profit is % of sell. Manual Bid enters the commercial price and derives profitability."
      />
      <Row gutter={16}>
        <Col span={12}>
          <Card size="small" title="Strategy">
            {ctx.readOnly ? (
              <Alert type="info" showIcon message="Approved — pricing read-only" />
            ) : (
              <Form form={form} layout="vertical" onFinish={(v) => m.mutate(v)}>
                <Form.Item name="pricing_method" label="Method" initialValue="TARGET_MARGIN">
                  <Select
                    options={[
                      { value: 'MARKUP', label: 'Markup' },
                      { value: 'TARGET_MARGIN', label: 'Target Margin' },
                      { value: 'MANUAL_BID_PRICE', label: 'Manual Bid Price' },
                      { value: 'HYBRID_ALLOCATION', label: 'Hybrid (manual bid + allocate)' },
                    ]}
                  />
                </Form.Item>
                {method === 'MARKUP' && (
                  <Form.Item name="markup_percent" label="Markup % (whole)">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                )}
                {method === 'TARGET_MARGIN' && (
                  <Form.Item name="target_margin_percent" label="Target Margin % (whole, &lt;100)">
                    <InputNumber min={0} max={99.9999} style={{ width: '100%' }} />
                  </Form.Item>
                )}
                {(method === 'MANUAL_BID_PRICE' || method === 'HYBRID_ALLOCATION') && (
                  <>
                    <Form.Item name="manual_bid_price" label="Manual Bid Price" rules={[{ required: true }]}>
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="manual_reason" label="Reason" rules={[{ required: true }]}>
                      <Input.TextArea rows={2} />
                    </Form.Item>
                  </>
                )}
                <Form.Item name="commercial_adjustment_amount" label="Commercial Adjustment">
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="commercial_adjustment_reason" label="Adjustment Reason">
                  <Input />
                </Form.Item>
                <Form.Item name="negotiation_reserve_amount" label="Negotiation Reserve (internal)">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="discount_type" label="Discount Type" initialValue="NONE">
                  <Select
                    options={[
                      { value: 'NONE', label: 'None' },
                      { value: 'PERCENT', label: 'Percent' },
                      { value: 'FIXED_AMOUNT', label: 'Fixed Amount' },
                    ]}
                  />
                </Form.Item>
                <Form.Item name="discount_percent" label="Discount %">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="discount_amount" label="Discount Amount">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={m.isPending}>
                  Save Pricing
                </Button>
              </Form>
            )}
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small" title="Commercial Bridge">
            <Paragraph type="secondary" style={{ marginBottom: 8 }}>
              Total Estimated Cost {money(summaryQ.data?.total_estimated_cost)}
            </Paragraph>
            <Table
              size="small"
              pagination={false}
              rowKey="k"
              dataSource={[
                { k: 'c', label: 'Total Estimated Cost', amt: bridge?.total_estimated_cost },
                { k: 'a', label: 'Commercial Adjustment', amt: bridge?.commercial_adjustment },
                { k: 'b', label: 'Pricing Basis', amt: bridge?.pricing_basis },
                { k: 'm', label: 'Method Sell', amt: bridge?.method_sell },
                { k: 'r', label: 'Negotiation Reserve', amt: bridge?.negotiation_reserve },
                { k: 'g', label: 'Gross Offer', amt: bridge?.gross_offer },
                { k: 'd', label: 'Discount', amt: bridge?.discount != null ? -Number(bridge.discount) : null },
                { k: 'f', label: 'Final Bid Price', amt: bridge?.final_bid_price },
                { k: 'p', label: 'Profit', amt: bridge?.profit },
              ]}
              columns={[
                { title: 'Layer', dataIndex: 'label' },
                { title: 'Amount', dataIndex: 'amt', render: money, width: 120 },
              ]}
            />
            <Space wrap style={{ marginTop: 12 }}>
              <Tag>Margin {pct(bridge?.margin_actual)}</Tag>
              <Tag>Markup {pct(bridge?.markup_actual)}</Tag>
              <Tag>Before disc. {pct(bridge?.margin_before_discount)}</Tag>
              <Tag color="orange">Erosion {bridge?.margin_erosion_pp ?? '—'} pp</Tag>
            </Space>
            {summaryQ.data?.stale && (
              <Alert
                style={{ marginTop: 12 }}
                type="warning"
                showIcon
                message="Cost changed after pricing — review required"
              />
            )}
          </Card>
        </Col>
      </Row>
    </Space>
  );
}

export function ClientPricingPanel({ ctx }: { ctx: Wave6Ctx }) {
  const qc = useQueryClient();
  const { clientQ, recQ } = useWave6Pricing(ctx);
  const [preview, setPreview] = React.useState<any>(null);
  const previewM = useMutation({
    mutationFn: () =>
      epcEstimationService.previewAllocateClientPricing(ctx.estimateId, ctx.revisionId, {
        method: 'PROPORTIONAL_TO_COST',
      }),
    onSuccess: (d) => setPreview(d),
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Preview failed'),
  });
  const applyM = useMutation({
    mutationFn: () =>
      epcEstimationService.allocateClientPricing(ctx.estimateId, ctx.revisionId, {
        method: 'PROPORTIONAL_TO_COST',
        previewToken: preview?.preview_token,
        costFingerprint: preview?.cost_fingerprint,
      }),
    onSuccess: () => {
      message.success('Allocation applied');
      setPreview(null);
      qc.invalidateQueries({ queryKey: ['epc-est-client-pricing'] });
      qc.invalidateQueries({ queryKey: ['epc-est-pricing-rec'] });
      qc.invalidateQueries({ queryKey: ['epc-est-readiness'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Apply failed'),
  });
  const rec = recQ.data;
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Alert
        type={rec?.reconciled ? 'success' : 'warning'}
        showIcon
        message={
          rec?.reconciled
            ? `Reconciled · Base BOQ ${money(rec.allocated_base_sell)} = Final Bid ${money(rec.final_bid_price)}`
            : `NOT RECONCILED · Diff ${money(rec?.difference)} · Missing ${rec?.missing_count ?? '—'}`
        }
        description={`Priced ${rec?.priced_count ?? 0} / ${rec?.base_line_count ?? 0} base lines · Options ${money(rec?.optional_sell)} (excluded from base)`}
      />
      {!ctx.readOnly && (
        <Space>
          <Button onClick={() => previewM.mutate()} loading={previewM.isPending}>
            Preview proportional allocate
          </Button>
          {preview && (
            <Button type="primary" onClick={() => applyM.mutate()} loading={applyM.isPending}>
              Apply ({preview.affected_count} lines · remaining {money(preview.remaining_to_allocate)})
            </Button>
          )}
        </Space>
      )}
      <Alert
        type="info"
        showIcon
        message="Bulk allocate is desktop-primary; locked / CLIENT_FIXED lines are preserved."
      />
      <Table
        size="small"
        rowKey="id"
        loading={clientQ.isLoading}
        dataSource={clientQ.data?.lines || []}
        pagination={{ pageSize: 25 }}
        columns={[
          { title: 'Item', dataIndex: 'item_number', width: 90 },
          { title: 'Description', dataIndex: 'description', ellipsis: true },
          { title: 'Mode', dataIndex: 'pricing_mode', width: 120 },
          {
            title: 'Lock',
            dataIndex: 'pricing_lock',
            width: 60,
            render: (v: any) => (Number(v) ? 'Y' : ''),
          },
          { title: 'Cost snap', dataIndex: 'allocated_cost_amount', width: 100, render: money },
          { title: 'Sell', dataIndex: 'sell_amount', width: 100, render: money },
          {
            title: 'Flags',
            width: 100,
            render: (_: any, r: any) => (
              <Space size={0}>
                {Number(r.is_optional) ? <Tag>OPT</Tag> : null}
                {Number(r.is_alternate) ? <Tag>ALT</Tag> : null}
                {Number(r.is_provisional) ? <Tag>PS</Tag> : null}
              </Space>
            ),
          },
        ]}
      />
    </Space>
  );
}

export function ProfitabilityPanel({ ctx }: { ctx: Wave6Ctx }) {
  const { summaryQ, clientQ } = useWave6Pricing(ctx);
  const bridge = summaryQ.data?.commercial_bridge;
  const lines = (clientQ.data?.lines || []).map((l: any) => {
    const cost = Number(l.allocated_cost_amount) || 0;
    const sell = Number(l.sell_amount) || 0;
    const profit = sell - cost;
    const margin = sell > 0 ? (profit / sell) * 100 : null;
    return { ...l, _profit: profit, _margin: margin, _neg: sell > 0 && sell < cost };
  });
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Card size="small">
        <Space wrap>
          <Tag>Profit {money(bridge?.profit)}</Tag>
          <Tag>Margin {pct(bridge?.margin_actual)}</Tag>
          <Tag>Markup {pct(bridge?.markup_actual)}</Tag>
        </Space>
      </Card>
      <Table
        size="small"
        rowKey="id"
        dataSource={lines}
        pagination={{ pageSize: 25 }}
        columns={[
          { title: 'Description', dataIndex: 'description', ellipsis: true },
          { title: 'Cost', dataIndex: 'allocated_cost_amount', width: 100, render: money },
          { title: 'Sell', dataIndex: 'sell_amount', width: 100, render: money },
          { title: 'Profit', dataIndex: '_profit', width: 100, render: money },
          {
            title: 'Margin',
            dataIndex: '_margin',
            width: 90,
            render: (v: number | null, r: any) =>
              r._neg ? <Tag color="red">{pct(v)}</Tag> : pct(v),
          },
        ]}
      />
    </Space>
  );
}

export function SensitivityPanel({ ctx }: { ctx: Wave6Ctx }) {
  const [form] = Form.useForm();
  const [result, setResult] = React.useState<any>(null);
  const m = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.pricingSensitivity(ctx.estimateId, ctx.revisionId, v),
    onSuccess: (d) => setResult(d),
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Failed'),
  });
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Alert type="info" showIcon message="Read-only scenarios — estimate is not mutated." />
      <Form
        form={form}
        layout="inline"
        onFinish={(v) => m.mutate(v)}
        initialValues={{ cost_delta_pct: 5, price_delta_pct: 0, target_margin_scenario: 8 }}
      >
        <Form.Item name="cost_delta_pct" label="Cost Δ %">
          <InputNumber />
        </Form.Item>
        <Form.Item name="price_delta_pct" label="Price Δ %">
          <InputNumber />
        </Form.Item>
        <Form.Item name="target_margin_scenario" label="Min margin scenario %">
          <InputNumber min={0} max={99} />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={m.isPending}>
          Run
        </Button>
      </Form>
      {result && (
        <>
          <Card size="small" title="Scenario">
            <Space wrap>
              <Tag>Cost {money(result.scenario?.cost)}</Tag>
              <Tag>Bid {money(result.scenario?.bid)}</Tag>
              <Tag>Profit {money(result.scenario?.profit)}</Tag>
              <Tag>Margin {pct(result.scenario?.margin)}</Tag>
            </Space>
            <div style={{ marginTop: 8 }}>
              Break-even {money(result.break_even_price)} · Min sell @ scenario margin{' '}
              {money(result.minimum_sell_for_margin)}
            </div>
          </Card>
          <Table
            size="small"
            rowKey="label"
            pagination={false}
            dataSource={result.matrix || []}
            columns={[
              { title: 'Scenario', dataIndex: 'label' },
              { title: 'Cost', dataIndex: 'cost', render: money },
              { title: 'Bid', dataIndex: 'bid', render: money },
              { title: 'Profit', dataIndex: 'profit', render: money },
              { title: 'Margin', dataIndex: 'margin', render: pct },
            ]}
          />
        </>
      )}
    </Space>
  );
}

export function BidReconciliationPanel({ ctx }: { ctx: Wave6Ctx }) {
  const { recQ, clientQ } = useWave6Pricing(ctx);
  const rec = recQ.data;
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Alert
        type={rec?.reconciled ? 'success' : 'warning'}
        showIcon
        message={
          rec?.reconciled
            ? `Reconciled · Base BOQ ${money(rec.allocated_base_sell)} = Final Bid ${money(rec.final_bid_price)}`
            : `NOT RECONCILED · Diff ${money(rec?.difference)} · Missing ${rec?.missing_count ?? '—'}`
        }
        description={`Priced ${rec?.priced_count ?? 0} / ${rec?.base_line_count ?? 0} base lines · Options ${money(rec?.optional_sell)} (excluded from base)`}
      />
      <Table
        size="small"
        rowKey="id"
        loading={clientQ.isLoading}
        dataSource={clientQ.data?.lines || []}
        pagination={{ pageSize: 25 }}
        columns={[
          { title: 'Item', dataIndex: 'item_number', width: 90 },
          { title: 'Description', dataIndex: 'description', ellipsis: true },
          { title: 'Sell', dataIndex: 'sell_amount', width: 100, render: money },
          { title: 'Cost snap', dataIndex: 'allocated_cost_amount', width: 100, render: money },
        ]}
      />
    </Space>
  );
}

export function Wave6PricingTab({ ctx }: { ctx: Wave6Ctx }) {
  const { summaryQ } = useWave6Pricing(ctx);
  const bridge = summaryQ.data?.commercial_bridge;
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Card size="small">
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Text type="secondary">Final Bid Price</Text>
            <Title level={3} style={{ margin: 0 }}>
              {money(bridge?.final_bid_price)} {summaryQ.data?.currency || ''}
            </Title>
            <Space wrap style={{ marginTop: 8 }}>
              <Tag>Cost {money(summaryQ.data?.total_estimated_cost)}</Tag>
              <Tag color="green">Profit {money(bridge?.profit)}</Tag>
              <Tag>Margin {pct(bridge?.margin_actual)}</Tag>
              <Tag>Markup {pct(bridge?.markup_actual)}</Tag>
              <Tag color={summaryQ.data?.commercial_pricing === 'READY' ? 'green' : 'orange'}>
                Pricing {summaryQ.data?.commercial_pricing || '…'}
              </Tag>
            </Space>
          </Col>
        </Row>
      </Card>
      <Tabs
        items={[
          { key: 'strategy', label: 'Strategy', children: <StrategyPanel ctx={ctx} /> },
          { key: 'client', label: 'Client Pricing', children: <ClientPricingPanel ctx={ctx} /> },
          { key: 'profit', label: 'Profitability', children: <ProfitabilityPanel ctx={ctx} /> },
          { key: 'sens', label: 'Sensitivity', children: <SensitivityPanel ctx={ctx} /> },
        ]}
      />
    </Space>
  );
}

export function Wave6OverviewPricingStrip({ ctx }: { ctx: Wave6Ctx | null }) {
  const { summaryQ } = useWave6Pricing(ctx);
  if (!ctx || !summaryQ.data) return null;
  const b = summaryQ.data.commercial_bridge;
  if (!b?.final_bid_price) return null;
  return (
    <Card size="small" style={{ marginBottom: 12 }}>
      <Space wrap>
        <Tag color="blue">Bid {money(b.final_bid_price)}</Tag>
        <Tag>Profit {money(b.profit)}</Tag>
        <Tag>Margin {pct(b.margin_actual)}</Tag>
        <Tag color={summaryQ.data.commercial_pricing === 'READY' ? 'green' : 'orange'}>
          Commercial {summaryQ.data.commercial_pricing}
        </Tag>
      </Space>
    </Card>
  );
}
